<div class="dashboard-shell">
  <div class="page-heading">
    <div>
      <h1 class="page-title">Dashboard</h1>
      <p class="page-subtitle">Overview of your bilateral trading operations</p>
    </div>
    <div class="period-pill" *ngIf="overview() as data">{{ data.selected_period_label }}</div>
  </div>

  <div class="loading-state" *ngIf="loading()">Loading dashboard overview...</div>

  <ng-container *ngIf="!loading() && overview() as data">
    <section class="summary-grid">
      <article class="summary-card">
        <div class="summary-icon blue"><lucide-icon [img]="ScrollTextIcon" [size]="18"></lucide-icon></div>
        <h3>Active LOA</h3>
        <div class="summary-value">{{ formatCompact(data.active_loa_total) }}</div>
        <p>Buy: {{ data.active_loa_buy }} | Sell: {{ data.active_loa_sell }} | Banking: {{ data.active_loa_banking }}</p>
      </article>

      <article class="summary-card">
        <div class="summary-icon green"><lucide-icon [img]="FileSpreadsheetIcon" [size]="18"></lucide-icon></div>
        <h3>Active Contracts</h3>
        <div class="summary-value success">{{ formatCompact(data.active_contract_total) }}</div>
        <p>STOA: {{ data.active_contract_stoa }} | MTOA: {{ data.active_contract_mtoa }} | LTOA: {{ data.active_contract_ltoa }}</p>
      </article>

      <article class="summary-card">
        <div class="summary-icon amber"><lucide-icon [img]="FilePlus2Icon" [size]="18"></lucide-icon></div>
        <h3>Applications</h3>
        <div class="summary-value warning">{{ formatCompact(data.application_total) }}</div>
        <p>{{ data.application_approved }} approved | {{ data.application_pending }} pending</p>
      </article>

      <article class="summary-card">
        <div class="summary-icon indigo"><lucide-icon [img]="ArrowRightLeftIcon" [size]="18"></lucide-icon></div>
        <h3>Total Power Flow</h3>
        <div class="summary-value">{{ formatMwh(data.total_power_flow_mwh) }}</div>
        <p>Aggregated power flow for the selected period</p>
      </article>

      <article class="summary-card">
        <div class="summary-icon rose"><lucide-icon [img]="HourglassIcon" [size]="18"></lucide-icon></div>
        <h3>Overdue Bills</h3>
        <div class="summary-value danger">{{ formatCrore(data.overdue_bills_amount) }}</div>
        <p>{{ data.overdue_bills_count }} invoice(s) beyond due cycle</p>
      </article>
    </section>

    <section class="content-grid">
      <article class="overview-card quick-actions-card">
        <div class="card-header">
          <h2>Quick Actions</h2>
        </div>
        <div class="quick-action-list">
          <a class="quick-action" *ngFor="let action of quickActions()" [routerLink]="action.route">
            <span class="quick-icon">
              <lucide-icon [img]="action.icon" [size]="16"></lucide-icon>
            </span>
            <span>
              <strong>{{ action.label }}</strong>
              <small>{{ action.subtitle }}</small>
            </span>
          </a>
        </div>
      </article>

      <article class="overview-card flow-card">
        <div class="card-header">
          <h2>Monthly Power Flow Analysis</h2>
          <div class="flow-controls">
            <select class="flow-select" [value]="flowGranularity()" (change)="onFlowGranularityChange(($any($event.target).value || '').toString())">
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        </div>
        <div class="chart-frame tall-chart">
          <canvas #monthlyFlowCanvas></canvas>
        </div>
      </article>

      <article class="overview-card">
        <div class="card-header">
          <h2>Transaction Type Analysis</h2>
        </div>
        <div class="chart-frame pie-chart">
          <canvas #transactionCanvas></canvas>
        </div>
      </article>
    </section>

    <section class="billing-panel">
      <div class="panel-header">
        <h2>Billing and Revenue Insights</h2>
      </div>

      <div class="billing-top-grid">
        <article class="overview-card billing-summary-card">
          <div class="card-header">
            <h2>Billing Summary</h2>
          </div>
          <div class="billing-summary-list">
            <div class="billing-summary-row">
              <span class="bullet sop"></span>
              <span class="label">SOP Amount</span>
              <strong>{{ formatCrore(data.billing_summary.sop_amount_cr) }}</strong>
            </div>
            <div class="billing-summary-row">
              <span class="bullet tmr"></span>
              <span class="label">TMR Amount</span>
              <strong>{{ formatCrore(data.billing_summary.tmr_amount_cr) }}</strong>
            </div>
            <div class="billing-summary-row">
              <span class="bullet oa"></span>
              <span class="label">OA Charges</span>
              <strong>{{ formatCrore(data.billing_summary.oa_charges_cr) }}</strong>
            </div>
            <div class="billing-summary-row">
              <span class="bullet surcharge"></span>
              <span class="label">Surcharge Generated</span>
              <strong>{{ formatCrore(data.billing_summary.surcharge_amount_cr) }}</strong>
            </div>
          </div>
          <div class="billing-total">
            <span>Total Revenue</span>
            <strong>{{ formatCrore(data.billing_summary.total_revenue_cr) }}</strong>
          </div>
        </article>

        <article class="overview-card billing-type-card">
          <div class="card-header">
            <h2>Billing by Type (₹ Crores)</h2>
            <div class="billing-filters">
              <span class="filter-pill">Last 4 Months</span>
              <span class="filter-pill">Auto</span>
            </div>
          </div>
          <div class="chart-frame billing-chart">
            <canvas #billingTypeCanvas></canvas>
          </div>
        </article>
      </div>

      <div class="billing-bottom-grid">
        <article class="overview-card payment-status-card">
          <div class="card-header">
            <h2>Payment Status</h2>
          </div>
          <div class="status-block">
            <div class="status-head">
              <span>Paid Amount</span>
              <strong>{{ formatCrore(data.payment_status.paid_amount_cr) }}</strong>
            </div>
            <div class="status-track">
              <div class="status-fill paid" [style.width.%]="paymentStatusPercent(data.payment_status.paid_amount_cr, data)"></div>
            </div>
          </div>
          <div class="status-block">
            <div class="status-head">
              <span>Due Amount</span>
              <strong>{{ formatCrore(data.payment_status.due_amount_cr) }}</strong>
            </div>
            <div class="status-track">
              <div class="status-fill due" [style.width.%]="paymentStatusPercent(data.payment_status.due_amount_cr, data)"></div>
            </div>
          </div>
          <div class="status-block">
            <div class="status-head">
              <span>Overdue Amount</span>
              <strong>{{ formatCrore(data.payment_status.overdue_amount_cr) }}</strong>
            </div>
            <div class="status-track">
              <div class="status-fill overdue" [style.width.%]="paymentStatusPercent(data.payment_status.overdue_amount_cr, data)"></div>
            </div>
          </div>
        </article>

        <article class="overview-card payment-trend-card">
          <div class="card-header">
            <h2>Outstanding vs Paid Trend (Last 6 Months)</h2>
          </div>
          <div class="chart-frame payment-chart">
            <canvas #paymentTrendCanvas></canvas>
          </div>
        </article>
      </div>
    </section>

    <section class="bottom-grid">
      <article class="overview-card wide-card">
        <div class="card-header">
          <div>
            <h2>Scheduled vs Actual Quantum</h2>
            <p class="card-meta">
              Scheduled {{ formatMwh(data.scheduled_total_mwh) }}
              <span>Actual {{ formatMwh(data.actual_total_mwh) }}</span>
              <span class="deviation-text">Deviation {{ data.deviation_percent }}%</span>
            </p>
          </div>
        </div>
        <div class="chart-frame tall-chart">
          <canvas #scheduledCanvas></canvas>
        </div>
      </article>

      <article class="overview-card donut-card">
        <div class="card-header">
          <h2>Contract Health</h2>
        </div>
        <div class="donut-layout">
          <div class="donut-wrap">
            <canvas #contractHealthCanvas></canvas>
            <div class="donut-center">
              <strong>{{ data.contract_health_total }}</strong>
              <span>Total</span>
            </div>
          </div>
        </div>
      </article>

      <article class="overview-card donut-card">
        <div class="card-header">
          <h2>Contract Type Distribution</h2>
        </div>
        <div class="donut-layout">
          <div class="donut-wrap compact">
            <canvas #contractTypeCanvas></canvas>
            <div class="donut-center">
              <strong>{{ formatCompact(contractTypeTotal(data.contract_type_distribution)) }}</strong>
              <span>Total contracts</span>
            </div>
          </div>
        </div>
        <div class="distribution-strip">
          <span>STOA {{ contractTypePercent(data.contract_type_distribution['STOA'] || 0, contractTypeTotal(data.contract_type_distribution)) }}%</span>
          <span>MTOA {{ contractTypePercent(data.contract_type_distribution['MTOA'] || 0, contractTypeTotal(data.contract_type_distribution)) }}%</span>
          <span>LTOA {{ contractTypePercent(data.contract_type_distribution['LTOA'] || 0, contractTypeTotal(data.contract_type_distribution)) }}%</span>
        </div>
      </article>
    </section>

    <section class="activity-grid">
      <article class="overview-card">
        <div class="card-header">
          <h2>Recent Transaction</h2>
        </div>
        <div class="recent-transaction-list">
          <div class="transaction-item" *ngFor="let item of data.recent_transactions">
            <div class="transaction-copy">
              <strong>{{ item.title }}</strong>
              <span>{{ item.subtitle }}</span>
              <small>{{ item.meta }}</small>
            </div>
            <span class="transaction-status" [class.approved]="recentTransactionStatusClass(item.status) === 'approved'" [class.pending]="recentTransactionStatusClass(item.status) === 'pending'">
              {{ item.status }}
            </span>
          </div>
        </div>
      </article>

      <article class="overview-card">
        <div class="card-header">
          <h2>Alerts & Notifications</h2>
        </div>
        <div class="alert-list">
          <div class="alert-item" *ngFor="let alert of data.alerts" [class.warning]="alertToneClass(alert.tone) === 'warning'" [class.danger]="alertToneClass(alert.tone) === 'danger'" [class.success]="alertToneClass(alert.tone) === 'success'" [class.info]="alertToneClass(alert.tone) === 'info'">
            <span class="alert-icon">◔</span>
            <div class="alert-copy">
              <strong>{{ alert.title }}</strong>
              <span>{{ alert.message }}</span>
            </div>
          </div>
        </div>
      </article>
    </section>
  </ng-container>
</div>
